import axios from "axios";
import store from "../store";
import { setError, setUserActiveSession, updateUserActiveSessionDelta } from "../store/actions";
import { getRandomUUID } from "../Token";
import { ConnectionRefusedError, NetworkError, ServerError } from "../utils/error";
import {
  CONTENT_TYPE_FORM,
  CONTENT_TYPE_IMAGE,
  CONTENT_TYPE_JSON,
  HEADER_AUTHORIZATION,
  HEADER_CONTENT_TYPE,
  HEADER_TRANSACTION_ID,
} from "./constants/apiConstants";
import { coordinatedRefresh } from "./refreshCoordinator";
import {
  ERROR_API_REQUEST_FAILED,
  ERROR_CODES,
  ERROR_INSUFFICIENT_ACCESS,
  ERROR_NETWORK,
  ERROR_NETWORK_DISCONNECTED,
  ERROR_REFRESH_TOKEN_EMPTY,
  ERROR_SERVER_STATUS,
  ERROR_SERVICE_UNREACHABLE,
  ERROR_TOKEN_REFRESH_FAILED,
  ERROR_UNKNOWN,
  EXPECT_UNDEFINED
} from "./constants/errorConstants";
import { clearAllCookies } from "./utility/Utils";
import { invalidateTokenApi } from "../api/SessionServiceManager";
import { ROUTES, UUID_REGEX } from "./constants/routesConstants";
import { STORAGE } from "./constants/storageConstants";

const AUTH_SESSION_KEYS = [
  STORAGE.ACCOUNT_DETAILS,
  STORAGE.ACCOUNT_ID,
  STORAGE.SELECTED_ROLE,
  STORAGE.ROLE_PERMISSIONS_LIST,
  STORAGE.UPDATED_DATE,
  STORAGE.RURL,
];
export const clearAuthStorage = () => {
  AUTH_SESSION_KEYS.forEach((key) => sessionStorage.removeItem(key));
};

const axiosInstance = axios.create({
  withCredentials: true,
});
let isRefreshing = false;
let failedQueue = [];
// Flip this to true during intentional logout so in-flight 401s don't kick off a refresh attempt
let isLogoutInProgress = false;
export const setLogoutInProgress = (value) => { isLogoutInProgress = value; };

axiosInstance.interceptors.request.use(
  (request) => {
    const activeSession = store?.getState()?.sessionReducer?.activeSession;
    const reduxSessionState = activeSession?.activeSession?.sessionState;
    const reduxIsRedisMode = activeSession?.activeSession?.isRedisMode;
    const reduxAccessToken = activeSession?.activeSession?.sessionToken;
    // const accountId = activeSession?.activeSession?.accountId;
    // const userId = activeSession?.activeSession?.userId;

    // Redis mode: the session cookie is HttpOnly so the browser attaches it automatically
    // Legacy mode: we have the access token, so attach it as a Bearer header
    if (reduxSessionState || reduxIsRedisMode) {
      // nothing to do — withCredentials handles the cookie
    } else if (reduxAccessToken) {
      request.headers[HEADER_AUTHORIZATION] = `Bearer ${reduxAccessToken}`;
    }

    // Don't set Content-Type for FormData — browser must set it with the multipart boundary
    if (!(request.data instanceof FormData)) {
      request.headers[HEADER_CONTENT_TYPE] = request.isFormUrlEncoded ? CONTENT_TYPE_FORM : request.isImage ? CONTENT_TYPE_IMAGE : CONTENT_TYPE_JSON;
    }

    let transactionId = request.headers[HEADER_TRANSACTION_ID];
    if (!transactionId || !UUID_REGEX.test(transactionId)) {
      transactionId = getRandomUUID();
    }

    // if (userId && !transactionId.includes(`:${userId}`)) {
    //   transactionId += `:${userId}`;
    // }
    // if (accountId && !transactionId.includes(`:${accountId}`)) {
    //   transactionId += `:${accountId}`;
    // }

    request.headers[HEADER_TRANSACTION_ID] = transactionId;
    // Store the ID on the config object for later use in the response interceptor
    request._transactionId = transactionId;

    return request;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};
export const setupResponseInterceptor = (dispatch) => {
  axiosInstance.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      console.log("Error", error);
      const errorMessage = error.message || ERROR_UNKNOWN;
      const originalRequest = error.config;
      const transactionId = originalRequest?._transactionId;
      if (error.response) {
        const status = error.response.status;
        if (status === 403) {
          dispatch(
            setError({
              status: status,
              message: ERROR_INSUFFICIENT_ACCESS
            })
          );
          return Promise.reject(error);
        } else if (status === 401 && !originalRequest._retry) {
          if (isLogoutInProgress) {
            return Promise.reject(error);
          }
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            }).then(() => {
              return axiosInstance(originalRequest);
            });
          }

          originalRequest._retry = true; // Mark the request as retried to avoid infinite loops.
          isRefreshing = true;
          try {
            const refreshToken = store.getState()?.sessionReducer?.activeSession?.activeSession?.refreshToken;

            if (refreshToken && refreshToken !== EXPECT_UNDEFINED) {
              // Legacy mode: coordinatedRefresh shares a lock with idleTimeModal so only
              // one refresh call goes out even if both fire at the same time (CWE-362, NPDIOTS-8800)
              const { id_token, access_token: newAccessToken, refresh_token: newRefreshToken } = await coordinatedRefresh(refreshToken);
              let activeSessionData = {
                sessionToken: newAccessToken,
                refreshToken: newRefreshToken,
                idToken: id_token
              };
              dispatch(updateUserActiveSessionDelta(activeSessionData));
              axiosInstance.defaults.headers.common[HEADER_AUTHORIZATION] = `Bearer ${newAccessToken}`;
              processQueue(null, newAccessToken);
            } else {
              // Redis mode — no refresh token on the frontend, so the session has expired
              throw new Error(ERROR_REFRESH_TOKEN_EMPTY);
            }

            return axiosInstance(originalRequest);
          } catch (refreshError) {
            // Mark logout in progress first so any queued 401s (e.g. the logout call itself)
            // get rejected right away instead of piling up and causing a deadlock
            isLogoutInProgress = true;
            // Drain the queue before doing anything async
            processQueue(refreshError, null);
            clearAllCookies();
            clearAuthStorage();
            const expiredSession = store.getState()?.sessionReducer?.activeSession?.activeSession;
            const isRedisMode = expiredSession?.isRedisMode || (expiredSession?.sessionState && !expiredSession?.sessionToken);
            if (!isRedisMode) {
              // Legacy mode: tell the backend to invalidate the session, but don't wait for it
              invalidateTokenApi(expiredSession).catch(() => {});
            }
            // Redis mode: the session is already gone on the backend, no need to call logout
            dispatch(
              setUserActiveSession({
                accountId: "",
                sessionToken: "",
                refreshToken: "",
                idToken: "",
                userName: ""
              })
            );
            if (window.location.pathname !== ROUTES.LOGIN && window.location.pathname !== ROUTES.DASHBOARD) {
              sessionStorage.setItem(STORAGE.RURL, window.location.pathname);
            }
            const reason = isRedisMode ? "session_expired" : "refresh_failed";
            window.location.replace(window.location.origin + ROUTES.LOGIN + "?reason=" + reason);
            return Promise.reject(new ServerError(ERROR_TOKEN_REFRESH_FAILED(refreshError.message), 401, transactionId));
          } finally {
            isRefreshing = false;
          }
        } else {
          // All other HTTP errors (400, 404, 500) are classified here.
          return Promise.reject(new ServerError(ERROR_SERVER_STATUS(status), status, transactionId, error.response));
        }
      } else if (error.request) {
        // Network errors are classified here.
        console.log("Network error occurred");
        // Since Axios often provides the same 'ERR_NETWORK' code for
        // true offline state AND connection refused state
        // Check for standard Axios network errors which are ambiguous
        // Axios uses ERR_NETWORK for both "truly offline" and "server unreachable" — use navigator.onLine to tell them apart
        if (error.code === ERROR_CODES.ERR_NETWORK || error.code === ERROR_CODES.ECONNREFUSED) {
          // CRITICAL CHECK: Use browser's API to determine TRUE offline status.
          // This is the definitive way to solve the ambiguity problem.
          const isClientOffline = typeof navigator !== EXPECT_UNDEFINED && !navigator.onLine;

          if (isClientOffline) {
            // 1. TRUE OFFLINE: The client has no connectivity.
            return Promise.reject(new NetworkError(ERROR_NETWORK_DISCONNECTED));
          } else {
            // 2. ONLINE BUT UNREACHABLE: Client is online, but server/link is dead.
            // This is the correct classification for a DNS/Connection Refused error when the Wi-Fi is fine.
            return Promise.reject(new ConnectionRefusedError(ERROR_SERVICE_UNREACHABLE, transactionId));
          }
        } else {
          // Fallback for any other network/timeout issues
          return Promise.reject(new NetworkError(ERROR_NETWORK));
        }
      } else {
        // All other request setup errors are classified here.
        return Promise.reject(new Error(ERROR_API_REQUEST_FAILED(errorMessage)));
      }
    }
  );
};

export default axiosInstance;
